import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoIosAppstore, IoIosArrowDown } from "react-icons/io";
import { FaWhatsapp, FaLinkedin, FaInstagram, FaFacebookF, FaPaperPlane, FaHandHoldingHeart, FaBolt, FaShieldAlt, FaEnvelope, FaCheckCircle } from "react-icons/fa";
import { BiLogoPlayStore } from "react-icons/bi";

// --- Assets ---
import LogoImg from './assets/img/logo.svg';
import AppPrint1 from './assets/img/pg/choce_user.jpeg';
import AppPrint2 from './assets/img/pg/provedor.jpeg';
import FotoMissao from './assets/img/pg/unnamed.jpg';
import FotoVisao from './assets/img/pg/Cabeleireiro.jpg';
import FotoObjetivo from './assets/img/pg/soldador.jpg';
import FotoSucesso from './assets/img/pg/Provider.png';
import Unim from './assets/img/pg/unnamed (1).jpg';

// --- Fix Definitivo para Ícones (TS & Lint) ---
const IconPlay = BiLogoPlayStore as any;
const IconApple = IoIosAppstore as any;
const IconZap = FaWhatsapp as any;
const IconIn = FaLinkedin as any;
const IconInsta = FaInstagram as any;
const IconFace = FaFacebookF as any;
const IconPlane = FaPaperPlane as any;
const IconArrow = IoIosArrowDown as any;
const IconHeart = FaHandHoldingHeart as any;
const IconBolt = FaBolt as any;
const IconShield = FaShieldAlt as any;
const IconMail = FaEnvelope as any;
const IconCheck = FaCheckCircle as any;

const COLORS = {
  primary: '#006FFD',
  highlightLight: '#EAF2FF',
  neutralBg: '#F8F9FE',
  neutralDark: '#1F2024',
  neutralText: '#71727A',
  border: '#E8E9F1',
  yellowPoint: '#FFB37C',
  grayChumbo: '#494A50'
};

const SOCIAL_LINKS = {
  instagram: "https://www.instagram.com/busca_ja",
  linkedin: "https://www.linkedin.com/company/busca-j%C3%A1/",
  facebook: "https://www.facebook.com/share/18LuJVeKHf/",
  whatsapp: "https://wa.me/258848143840"
};

const FAQ_DATA = [
  { q: "Como funciona o cadastro de profissional?", a: "Para garantir a segurança, todos os provedores devem enviar a foto do BI e aguardar a aprovação administrativa antes de publicar serviços." },
  { q: "Quais são os métodos de pagamento?", a: "O sistema aceita pagamentos via M-Pesa, e-Mola e também pagamentos em espécie diretamente ao prestador." },
  { q: "O app funciona em todo o país?", a: "Sim. O BuscaJá utiliza geolocalização por latitude e longitude para encontrar o profissional mais próximo da sua rua ou bairro." },
  { q: "Como funcionam as solicitações de emergência?", a: "Existem dois tipos de chamados: Normal e Emergência. Chamados de emergência alertam provedores online para um atendimento imediato." }
];

const App: React.FC = () => {
  const [activeFaq, setActiveFaq] = useState<number | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const planetsData = useMemo(() => [
    { img: FotoMissao, color: '#FFB37C', label: 'Clientes' },
    { img: FotoVisao, color: '#25D366', label: 'Beleza' },
    { img: FotoObjetivo, color: '#FFD700', label: 'Técnicos' },
    { img: FotoSucesso, color: '#006FFD', label: 'Sucesso' },
    { img: Unim, color: '#4c5e75', label: 'Conexão' }
  ], []);

  useEffect(() => {
    let frameId: number;
    const animate = () => {
      setRotation(prev => prev + 0.3);
      frameId = requestAnimationFrame(animate);
    };
    frameId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frameId);
  }, []);

  const handleFakeSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowToast(true);
    setTimeout(() => setShowToast(false), 4000);
  };

  return (
    <div style={{ backgroundColor: '#fff', color: COLORS.neutralDark, minHeight: '100vh', overflowX: 'hidden' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;600;700;800&display=swap');
        * { box-sizing: border-box; font-family: 'Inter', sans-serif; margin: 0; padding: 0; scroll-behavior: smooth; }
        
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: ${COLORS.neutralBg}; }
        ::-webkit-scrollbar-thumb { background: ${COLORS.primary}; border-radius: 10px; }

        .btn-store { background: ${COLORS.neutralDark}; color: white; padding: 12px 24px; border-radius: 12px; display: flex; align-items: center; gap: 12px; border: none; cursor: pointer; transition: 0.3s; }
        .btn-store:hover { transform: translateY(-3px); box-shadow: 0 10px 20px rgba(0,0,0,0.1); }

        .plan-card { background: #fff; border: 1px solid ${COLORS.border}; padding: 45px 30px; border-radius: 24px; text-align: center; transition: all 0.4s ease; position: relative; }
        .plan-card:hover { border-color: ${COLORS.primary}; transform: translateY(-10px); }
        .plan-card-featured { border: 2px solid ${COLORS.primary}; box-shadow: 0 20px 40px rgba(0,111,253,0.1); }

        .btn-plan { width: 100%; padding: 15px; border-radius: 12px; border: 2px solid ${COLORS.primary}; background: transparent; color: ${COLORS.primary}; font-weight: 700; cursor: pointer; transition: 0.3s; }
        .btn-plan:hover { background: ${COLORS.primary}; color: white; }
        .btn-plan-active { background: ${COLORS.primary}; color: white; border: none; }

        .universe-container { height: 550px; width: 100%; position: relative; display: flex; justify-content: center; align-items: center; perspective: 1000px; overflow: hidden; }
        .planet-sphere { position: absolute; width: 220px; height: 220px; border-radius: 50%; overflow: hidden; cursor: pointer; }
        .planet-sphere img { width: 100%; height: 100%; object-fit: cover; }
        .planet-label { position: absolute; bottom: 20px; left: 50%; transform: translateX(-50%); background: ${COLORS.neutralDark}; color: white; padding: 5px 15px; border-radius: 20px; font-size: 12px; font-weight: 700; opacity: 0; transition: 0.3s; }
        .planet-sphere:hover .planet-label { opacity: 1; }

        .faq-item { border-bottom: 1px solid ${COLORS.border}; padding: 20px 0; cursor: pointer; }
        .value-card { padding: 30px; border-radius: 24px; background: white; border: 1px solid ${COLORS.border}; transition: 0.3s; }
        .value-card:hover { border-color: ${COLORS.primary}; transform: translateY(-5px); }

        .input-contact { width: 100%; padding: 15px; border-radius: 12px; border: 1px solid ${COLORS.border}; background: ${COLORS.neutralBg}; outline: none; transition: 0.2s; }
        .input-contact:focus { border-color: ${COLORS.primary}; background: #fff; }

        .animated-slogan { background: linear-gradient(90deg, #1F2024, #006FFD, #1F2024); background-size: 200% auto; -webkit-background-clip: text; -webkit-text-fill-color: transparent; animation: shine 5s linear infinite; }
        @keyframes shine { to { background-position: 200% center; } }

        @media (max-width: 640px) {
          .nav-links { display: none !important; }
          .hero-phones { gap: 12px !important; }
          .hero-phone { width: 150px !important; height: 300px !important; border-radius: 24px !important; }
          .planet-sphere { width: 110px !important; height: 110px !important; }
          .universe-container { height: 280px !important; }
          .section-heading { font-size: 24px !important; }
          .hero-tagline { font-size: 15px !important; }
        }

        @media (max-width: 480px) {
          .value-card { padding: 20px !important; }
          .plan-card { padding: 30px 20px !important; }
        }
      `}</style>

      <AnimatePresence>
        {showToast && (
          <motion.div initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }} style={{ position: 'fixed', bottom: '100px', left: '50%', transform: 'translateX(-50%)', background: '#25D366', color: 'white', padding: '15px 30px', borderRadius: '50px', zIndex: 9999, display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}>
            <IconCheck size={20} /> Mensagem enviada com sucesso!
          </motion.div>
        )}
      </AnimatePresence>

      <nav style={{ padding: '0 5%', height: '80px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${COLORS.border}`, background: 'white', position: 'sticky', top: 0, zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img src={LogoImg} alt="Logo" style={{ width: '35px' }} />
          <div style={{ fontWeight: 800, fontSize: '22px' }}>Busca<span style={{ color: COLORS.primary }}>Já</span><span style={{ color: COLORS.yellowPoint }}>.</span></div>
        </div>
        <div className="nav-links" style={{ display: 'flex', gap: '30px', alignItems: 'center' }}>
          <a href="#solucoes" style={{ textDecoration: 'none', color: COLORS.grayChumbo, fontSize: '14px', fontWeight: 600 }}>Soluções</a>
          <a href="#planos" style={{ textDecoration: 'none', color: COLORS.grayChumbo, fontSize: '14px', fontWeight: 600 }}>Planos</a>
          <a href="#contacto" style={{ textDecoration: 'none', color: COLORS.grayChumbo, fontSize: '14px', fontWeight: 600 }}>Contacto</a>
          <button style={{ background: COLORS.primary, color: 'white', border: 'none', padding: '10px 24px', borderRadius: '50px', fontWeight: 600 }}>Entrar</button>
        </div>
      </nav>

      <section style={{ padding: '80px 5% 40px', textAlign: 'center', background: COLORS.neutralBg }}>
        <motion.h1 className="animated-slogan" style={{ fontSize: 'clamp(28px, 6vw, 44px)', fontWeight: 800, marginBottom: '20px', letterSpacing: '-2px', lineHeight: 1.1 }}>Conectando talentos a oportunidades</motion.h1>
        <p className="hero-tagline" style={{ color: COLORS.grayChumbo, fontSize: '18px', maxWidth: '600px', margin: '0 auto 40px' }}>Encontre o que precisa, ofereça o que faz. A maior vitrine de serviços de Moçambique com a conexão que você busca</p>
        <div className="hero-phones" style={{ display: 'flex', gap: '20px', justifyContent: 'center', marginBottom: '60px', flexWrap: 'wrap' }}>
          <div className="hero-phone" style={{ width: '250px', height: '500px', background: '#000', borderRadius: '35px', border: `8px solid ${COLORS.neutralDark}`, overflow: 'hidden' }}><img src={AppPrint1} alt="Ecrã de escolha de utilizador no BuscaJá" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
          <div className="hero-phone" style={{ width: '250px', height: '500px', background: '#000', borderRadius: '35px', border: `8px solid ${COLORS.neutralDark}`, overflow: 'hidden' }}><img src={AppPrint2} alt="Ecrã de prestador de serviços no BuscaJá" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
        </div>
        <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button className="btn-store"><IconPlay size={24} /> <div><small style={{fontSize: '10px'}}>Disponível no</small><br/><b>Google Play</b></div></button>
          <button className="btn-store"><IconApple size={24} /> <div><small style={{fontSize: '10px'}}>Baixar na</small><br/><b>App Store</b></div></button>
        </div>
      </section>

      <section id="solucoes" style={{ padding: '100px 5%' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
          <div className="value-card"><IconHeart size={40} color={COLORS.primary} style={{ marginBottom: '20px' }} /><h4>Impacto Social</h4><p>Damos voz e visibilidade para quem está fora do mercado formal.</p></div>
          <div className="value-card"><IconBolt size={40} color={COLORS.yellowPoint} style={{ marginBottom: '20px' }} /><h4>Urgência</h4><p>Surgiu um problema? Encontre profissionais prontos agora mesmo.</p></div>
          <div className="value-card"><IconShield size={40} color={COLORS.primary} style={{ marginBottom: '20px' }} /><h4>Segurança</h4><p>Verificação obrigatória de BI para todos os provedores.</p></div>
        </div>
      </section>

      <section style={{ padding: '60px 5%', textAlign: 'center', background: COLORS.neutralDark, color: 'white' }}>
        <h2 style={{ fontSize: '30px', fontWeight: 800, marginBottom: '40px' }}>Um universo de oportunidades e várias ofertas na sua mão.</h2>
        <div className="universe-container">
          {planetsData.map((planet, i) => {
            const angle = (i * (360 / planetsData.length) + rotation) % 360;
            const rad = (angle * Math.PI) / 180;
            const orbitRadius = Math.min(windowWidth * 0.36, 380);
            const x = Math.sin(rad) * orbitRadius;
            const z = Math.cos(rad) * 200;
            const scale = (z + 300) / 450;
            return (
              <motion.div key={i} className="planet-sphere" style={{ x, zIndex: Math.round(z + 500), scale, opacity: (z + 250) / 450, boxShadow: `0 20px 50px rgba(0,0,0,0.1), 0 0 40px ${planet.color}40` }}>
                <img src={planet.img} alt={planet.label} />
                <div className="planet-label">{planet.label}</div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section id="planos" style={{ padding: '100px 5%', background: COLORS.neutralBg }}>
        <h2 className="section-heading" style={{ textAlign: 'center', marginBottom: '50px', fontSize: '32px', fontWeight: 800 }}>Planos de Assinatura</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '30px', maxWidth: '1200px', margin: '0 auto' }}>
          {[
            { t: 'Mensal', p: '74,00 MT', d: '30 dias de visibilidade', f: false },
            { t: 'Trimestral', p: '223,00 MT', d: '90 dias de visibilidade', f: true },
            { t: 'Semestral', p: '445,50 MT', d: '180 dias de visibilidade', f: false },
            { t: 'Anual', p: '832,00 MT', d: '365 dias de visibilidade', f: false }
          ].map((plan, i) => (
            <div key={i} className={`plan-card ${plan.f ? 'plan-card-featured' : ''}`}>
              {plan.f && <span style={{ position: 'absolute', top: '-15px', left: '50%', transform: 'translateX(-50%)', background: COLORS.primary, color: 'white', padding: '6px 20px', borderRadius: '50px', fontSize: '11px', fontWeight: 800 }}>RECOMENDADO</span>}
              <p style={{ fontSize: '12px', fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', marginBottom: '20px' }}>{plan.t}</p>
              <div style={{ marginBottom: '20px' }}><span style={{ fontSize: '32px', fontWeight: 800 }}>{plan.p}</span></div>
              <p style={{ fontSize: '14px', color: COLORS.grayChumbo, marginBottom: '25px' }}>{plan.d}</p>
              <button className={`btn-plan ${plan.f ? 'btn-plan-active' : ''}`}>Escolher Plano</button>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" style={{ padding: '100px 5%' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <h2 className="section-heading" style={{ textAlign: 'center', marginBottom: '40px', fontSize: '32px', fontWeight: 800 }}>Dúvidas Comuns</h2>
          {FAQ_DATA.map((item, i) => (
            <div key={i} className="faq-item" onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ fontSize: '17px', fontWeight: 600 }}>{item.q}</h4>
                <motion.div animate={{ rotate: activeFaq === i ? 180 : 0 }}><IconArrow size={18} /></motion.div>
              </div>
              <AnimatePresence>
                {activeFaq === i && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} style={{ overflow: 'hidden' }}>
                    <p style={{ paddingTop: '15px', color: COLORS.grayChumbo, fontSize: '15px' }}>{item.a}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          ))}
        </div>
      </section>

      <section id="contacto" style={{ padding: '100px 5%', background: COLORS.neutralBg }}>
        <div style={{ maxWidth: '600px', margin: '0 auto', textAlign: 'center' }}>
          <h2 className="section-heading" style={{ fontSize: '32px', fontWeight: 800, marginBottom: '30px' }}>Fale Conosco</h2>
          <form onSubmit={handleFakeSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px', textAlign: 'left' }}>
            <input type="text" placeholder="Nome completo" className="input-contact" required />
            <input type="email" placeholder="E-mail" className="input-contact" required />
            <textarea placeholder="Mensagem..." rows={4} className="input-contact" style={{ resize: 'none' }} required></textarea>
            <button type="submit" style={{ background: COLORS.primary, color: 'white', padding: '16px', borderRadius: '50px', border: 'none', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
              <IconPlane /> Enviar Agora
            </button>
          </form>
        </div>
      </section>

      <footer style={{ padding: '80px 5% 40px', background: COLORS.neutralDark, color: 'white' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '40px' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: '24px', marginBottom: '15px' }}>BuscaJá<span style={{ color: COLORS.yellowPoint }}>.</span></div>
            <p style={{ opacity: 0.6 }}>O talento de Moçambique num só lugar.</p>
          </div>
          <div>
            <h4 style={{ marginBottom: '20px' }}>Siga-nos</h4>
            <div style={{ display: 'flex', gap: '20px' }}>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}><IconInsta size={24} /></a>
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}><IconFace size={24} /></a>
              <a href={SOCIAL_LINKS.linkedin} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}><IconIn size={24} /></a>
            </div>
          </div>
          <div>
            <h4 style={{ marginBottom: '20px' }}>Contacto</h4>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: COLORS.primary }}><IconMail /> admin@buscajá.com</div>
          </div>
        </div>
        <div style={{ textAlign: 'center', marginTop: '60px', fontSize: '12px', opacity: 0.3 }}>© 2026 BuscaJá. Todos os direitos reservados.</div>
      </footer>

      <motion.a href={SOCIAL_LINKS.whatsapp} target="_blank" rel="noreferrer" whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} style={{ position: 'fixed', bottom: '30px', right: '30px', background: '#25D366', width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', boxShadow: '0 10px 20px rgba(0,0,0,0.2)', zIndex: 1000 }}>
        <IconZap size={30} />
      </motion.a>
    </div>
  );
};

export default App;